/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Pause as PauseIcon,
  Play,
  CheckCircle2,
  XCircle,
  CreditCard,
  MessageSquare,
  Pencil,
  Home,
} from "lucide-react";
import { propertiesApi, photoUrl, startIciciPayment } from "./api";
import { formatInr } from "../utils/inrFormat";
import "./MyListings.css";

const TABS = [
  { id: "all", label: "All" },
  { id: "ACTIVE", label: "Active" },
  { id: "PAUSED", label: "Paused" },
  { id: "DRAFT", label: "Drafts" },
  { id: "PENDING_PAYMENT", label: "Pending Payment" },
  { id: "EXPIRED", label: "Expired" },
  { id: "CLOSED_WON", label: "Closed Won" },
  { id: "CLOSED_LOST", label: "Closed Lost" },
];

export default function MyListings() {
  const [tab, setTab] = useState("all");
  const [listings, setListings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [pane, setPane] = useState("listings"); // 'listings' | 'inquiries'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ls, inq] = await Promise.all([
        propertiesApi.myListings({
          status: tab === "all" ? undefined : tab,
        }),
        propertiesApi.myInquiries(),
      ]);
      setListings(ls || []);
      setInquiries(inq || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const newInquiries = inquiries.filter((i) => i.status === "NEW").length;

  // After ICICI redirects back through the backend, we land here with
  // ?payment=success|failed. Surface a banner, then strip the param.
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentResult = searchParams.get("payment");
  const dismissPayment = () => {
    searchParams.delete("payment");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="vsp-my-shell">
      {paymentResult === "success" && (
        <div className="vsp-my-pay-banner success" role="status">
          <CheckCircle2 size={16} /> Payment received — your listing is now live.
          <button onClick={dismissPayment} aria-label="Dismiss">×</button>
        </div>
      )}
      {paymentResult === "failed" && (
        <div className="vsp-my-pay-banner failed" role="alert">
          <XCircle size={16} /> Payment didn't go through. Your listing is under
          “Pending Payment” — use “Pay &amp; Publish” to try again.
          <button onClick={dismissPayment} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="vsp-my-head">
        <div>
          <h1>My Listings</h1>
          <p className="vsp-my-sub">
            Listings on villas you own + the inquiries you've received.
          </p>
        </div>
        <button
          className="vsp-my-add"
          onClick={() => navigate("/properties/new")}
        >
          <Plus size={14} /> New listing
        </button>
      </div>

      <div className="vsp-my-tabs">
        <button
          className={pane === "listings" ? "active" : ""}
          onClick={() => setPane("listings")}
        >
          Listings ({listings.length})
        </button>
        <button
          className={pane === "inquiries" ? "active" : ""}
          onClick={() => setPane("inquiries")}
        >
          Inquiries
          {newInquiries > 0 && (
            <span className="vsp-my-tab-badge">{newInquiries} new</span>
          )}
        </button>
      </div>

      {pane === "listings" && (
        <ListingsPane
          tab={tab}
          setTab={setTab}
          listings={listings}
          loading={loading}
          error={error}
          onChanged={load}
        />
      )}

      {pane === "inquiries" && (
        <InquiriesPane
          inquiries={inquiries}
          loading={loading}
          error={error}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ListingsPane({ tab, setTab, listings, loading, error, onChanged }) {
  const navigate = useNavigate();

  const onPause = async (id) => {
    try {
      await propertiesApi.pause(id);
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };
  const onResume = async (id) => {
    try {
      await propertiesApi.resume(id);
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };
  const onPublish = async (id) => {
    try {
      const res = await propertiesApi.publish(id);
      // Publishing / renewing collects the ₹100 fee via ICICI PgPay —
      // this redirects the browser to the ICICI hosted page.
      if (res && res.payment) {
        startIciciPayment(res.payment);
        return;
      }
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };
  const onClose = async (id, outcome) => {
    if (!window.confirm(`Close listing as ${outcome}?`)) return;
    try {
      await propertiesApi.close(id, outcome);
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <>
      <div className="vsp-my-status-row">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`vsp-my-status-pill ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="vsp-my-error">{error}</div>}
      {loading && <div className="vsp-my-empty">Loading…</div>}

      {!loading && listings.length === 0 && (
        <div className="vsp-my-empty-card">
          <Home size={28} strokeWidth={1.4} />
          <h3>No listings here</h3>
          <p>Create your first listing — it's free and live in a minute.</p>
        </div>
      )}

      {listings.length > 0 && (
        <div className="vsp-my-list">
          {listings.map((l) => (
            <div key={l.id} className="vsp-my-row">
              <Link to={`/properties/${l.id}`} className="vsp-my-thumb">
                {l.primary_photo_url ? (
                  <img src={photoUrl(l.primary_photo_url)} alt="" />
                ) : (
                  <div className="vsp-my-thumb-empty">
                    <Home size={20} />
                  </div>
                )}
              </Link>
              <div className="vsp-my-row-body">
                <div className="vsp-my-row-head">
                  <Link to={`/properties/${l.id}`} className="vsp-my-row-title">
                    {l.villa?.villa_number}
                  </Link>
                  <span className={`vsp-my-status status-${l.status.toLowerCase()}`}>
                    {l.status}
                  </span>
                </div>
                <div className="vsp-my-row-meta">
                  {l.type === "RENT"
                    ? formatInr(l.monthly_rent_inr, { monthly: true })
                    : formatInr(l.expected_price_inr)}{" "}
                  · {l.bedrooms} BHK · {l.built_up_area_sqft.toLocaleString("en-IN")} sqft
                </div>
                {l.new_inquiry_count > 0 ? (
                  <div className="vsp-my-row-inq">
                    <MessageSquare size={12} /> {l.new_inquiry_count} new of {l.inquiry_count} inquiries
                  </div>
                ) : (
                  <div className="vsp-my-row-inq muted">
                    {l.inquiry_count || 0} total inquiries
                  </div>
                )}
                {l.expires_at && (
                  <div className="vsp-my-row-expires">
                    Expires {new Date(l.expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="vsp-my-row-actions">
                {l.status === "DRAFT" && (
                  <button
                    className="vsp-my-btn-primary"
                    onClick={() => onPublish(l.id)}
                  >
                    Publish
                  </button>
                )}
                {l.status === "ACTIVE" && (
                  <button
                    className="vsp-my-btn-secondary"
                    onClick={() => onPause(l.id)}
                    title="Pause"
                  >
                    <PauseIcon size={12} /> Pause
                  </button>
                )}
                {l.status === "PAUSED" && (
                  <button
                    className="vsp-my-btn-secondary"
                    onClick={() => onResume(l.id)}
                    title="Resume"
                  >
                    <Play size={12} /> Resume
                  </button>
                )}
                {l.status === "PENDING_PAYMENT" && (
                  <button
                    className="vsp-my-btn-primary"
                    onClick={() => onPublish(l.id)}
                    title="Complete the ₹100 publishing fee"
                  >
                    <CreditCard size={12} /> Pay &amp; Publish
                  </button>
                )}
                {l.status === "EXPIRED" && (
                  <button
                    className="vsp-my-btn-primary"
                    onClick={() => onPublish(l.id)}
                  >
                    Renew & Republish
                  </button>
                )}
                {(l.status === "ACTIVE" || l.status === "PAUSED") && (
                  <button
                    className="vsp-my-btn-success"
                    onClick={() => onClose(l.id, "WON")}
                    title="Mark as rented/sold"
                  >
                    <CheckCircle2 size={12} /> Mark closed
                  </button>
                )}
                {l.status === "DRAFT" && (
                  <button
                    className="vsp-my-btn-secondary"
                    onClick={() => navigate(`/properties/${l.id}/edit`)}
                  >
                    <Pencil size={12} /> Edit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const INQ_STATUSES = [
  ["NEW", "New", "blue"],
  ["CONTACTED", "Contacted", "yellow"],
  ["SCHEDULED", "Visit Scheduled", "green"],
  ["NEGOTIATING", "Negotiating", "purple"],
  ["CLOSED_WON", "Closed (Won)", "emerald"],
  ["CLOSED_LOST", "Closed (Lost)", "gray"],
];

function InquiriesPane({ inquiries, loading, error, onChanged }) {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all"
    ? inquiries
    : inquiries.filter((i) => i.status === filter);

  return (
    <>
      <div className="vsp-my-status-row">
        <button
          className={`vsp-my-status-pill ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          All
        </button>
        {INQ_STATUSES.map(([k, label]) => (
          <button
            key={k}
            className={`vsp-my-status-pill ${filter === k ? "active" : ""}`}
            onClick={() => setFilter(k)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <div className="vsp-my-error">{error}</div>}
      {loading && <div className="vsp-my-empty">Loading…</div>}

      {!loading && visible.length === 0 && (
        <div className="vsp-my-empty-card">
          <MessageSquare size={28} strokeWidth={1.4} />
          <h3>No inquiries yet</h3>
          <p>Once members reach out, you'll see them here.</p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="vsp-my-inq-list">
          {visible.map((i) => (
            <InquiryRow key={i.id} inquiry={i} onChanged={onChanged} />
          ))}
        </div>
      )}
    </>
  );
}

function InquiryRow({ inquiry: i, onChanged }) {
  const [showScheduler, setShowScheduler] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [note, setNote] = useState(i.owner_notes || "");

  const setStatus = async (status) => {
    try {
      await propertiesApi.patchInquiry(i.id, { status });
      onChanged();
    } catch (e) {
      alert(e.message);
    }
  };

  const saveNote = async () => {
    if (note.trim() === (i.owner_notes || "")) return;
    setSavingNote(true);
    try {
      await propertiesApi.patchInquiry(i.id, { owner_notes: note.trim() || null });
      onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingNote(false);
    }
  };

  const visitISO = i.visit_at ? new Date(i.visit_at) : null;
  const visitLabel = visitISO
    ? visitISO.toLocaleString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className="vsp-my-inq">
      <div className="vsp-my-inq-head">
        <div className="vsp-my-inq-from">
          <div className="vsp-my-inq-avatar">
            {(i.inquirer.name || "?")[0]}
          </div>
          <div>
            <strong>{i.inquirer.name}</strong>
            <div className="vsp-my-inq-from-meta">
              {i.property?.villa_number} ·{" "}
              {new Date(i.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>
        <select
          className="vsp-my-inq-status"
          value={i.status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {INQ_STATUSES.map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
      </div>
      {i.message && <div className="vsp-my-inq-msg">"{i.message}"</div>}
      {visitLabel && (
        <div className="vsp-my-inq-visit-pill">
          📅 Visit scheduled — {visitLabel}
        </div>
      )}
      <div className="vsp-my-inq-contact">
        <a href={`tel:+91${i.inquirer.phone}`}>
          Call +91 {i.inquirer.phone}
        </a>
        {i.inquirer.email && (
          <a href={`mailto:${i.inquirer.email}`}>
            Email {i.inquirer.email}
          </a>
        )}
        <a
          href={`https://wa.me/91${i.inquirer.phone}`}
          target="_blank"
          rel="noreferrer"
        >
          WhatsApp
        </a>
        <button
          type="button"
          className="vsp-my-inq-action"
          onClick={() => setShowScheduler((v) => !v)}
        >
          {visitLabel ? "Reschedule visit" : "Schedule visit"}
        </button>
      </div>
      {showScheduler && (
        <VisitScheduler
          inquiry={i}
          onClose={() => setShowScheduler(false)}
          onSaved={() => {
            setShowScheduler(false);
            onChanged();
          }}
        />
      )}
      <div className="vsp-my-inq-notes">
        <label>Owner notes (private)</label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          placeholder="Internal notes about this inquiry…"
        />
        {savingNote && <span className="vsp-my-inq-savestate">Saving…</span>}
      </div>
    </div>
  );
}

function VisitScheduler({ inquiry, onClose, onSaved }) {
  // Default to tomorrow at 11am.
  const defaultDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(11, 0, 0, 0);
    return {
      date: d.toISOString().slice(0, 10),
      time: "11:00",
    };
  })();
  const initial = inquiry.visit_at
    ? (() => {
        const d = new Date(inquiry.visit_at);
        const pad = (n) => String(n).padStart(2, "0");
        return {
          date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
        };
      })()
    : defaultDate;
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [notes, setNotes] = useState(inquiry.visit_notes || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (!date || !time) {
      setError("Pick a date and time");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const iso = new Date(`${date}T${time}:00`).toISOString();
      await propertiesApi.patchInquiry(inquiry.id, {
        visit_at: iso,
        visit_notes: notes.trim() || null,
        status: "SCHEDULED",
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Could not schedule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="vsp-my-inq-sched" onSubmit={submit}>
      <div className="vsp-my-sched-row">
        <label>
          <span>Date</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </label>
        <label>
          <span>Time</span>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
          />
        </label>
      </div>
      <label className="vsp-my-sched-notes-label">
        <span>Notes for the visit (optional)</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Come to the back gate; meet the caretaker first"
        />
      </label>
      {error && <div className="vsp-my-sched-err">{error}</div>}
      <div className="vsp-my-sched-actions">
        <button
          type="button"
          className="vsp-my-btn-secondary"
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="vsp-my-btn-primary"
          disabled={busy}
        >
          {busy ? "Saving…" : "Save & mark scheduled"}
        </button>
      </div>
    </form>
  );
}
